import React, { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import LeadModal from "./LeadModal";
import { copyText, sources } from "./leadShared";
import { showError, showSuccess } from "../ToastNotifications";

const trackedUrl = (base, campaign, source) => {
  if (!base) return "";
  const url = new URL(base);
  if (campaign.trim()) url.searchParams.set("utm_campaign", campaign.trim());
  if (source && source !== "Form Link") url.searchParams.set("source", source);
  return url.toString();
};

// Everything needed to distribute a published capture form: tracked link, QR code, embed code and share targets.
const ShareFormModal = ({ form, onClose, justPublished = false }) => {
  const [campaign, setCampaign] = useState("");
  const [source, setSource] = useState("Form Link");
  const [tab, setTab] = useState("link");
  const qrRef = useRef(null);
  const link = useMemo(() => { try { return trackedUrl(form.publicUrl, campaign, source); } catch { return form.publicUrl || ""; } }, [form.publicUrl, campaign, source]);
  const embed = `<iframe src="${link}" title="${(form.name || "Lead form").replaceAll('"', "&quot;")}" width="100%" height="720" style="border:0;max-width:640px" loading="lazy"></iframe>`;
  const live = form.enabled !== false && form.linkEnabled !== false;
  const copy = async (text, message) => {
    try { await copyText(text); showSuccess(message); }
    catch (error) { showError(error.message); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: form.name, url: link }); return; }
      catch (error) { if (error.name === "AbortError") return; }
    }
    copy(link, "Capture link copied.");
  };
  const downloadQr = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" }));
    const anchor = document.createElement("a");
    anchor.href = url; anchor.download = `${(form.name || "lead-form").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-qr.svg`; anchor.click();
    URL.revokeObjectURL(url);
  };
  const encoded = encodeURIComponent(link);
  const text = encodeURIComponent(form.name || "Get in touch");
  const targets = [
    ["WhatsApp", "ti-brand-whatsapp", `https://wa.me/?text=${text}%20${encoded}`],
    ["LinkedIn", "ti-brand-linkedin", `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`],
    ["X", "ti-brand-x", `https://twitter.com/intent/tweet?text=${text}&url=${encoded}`],
    ["Facebook", "ti-brand-facebook", `https://www.facebook.com/sharer/sharer.php?u=${encoded}`],
    ["Email", "ti-mail", `mailto:?subject=${text}&body=${encoded}`]
  ];

  return <LeadModal size="md" onClose={onClose} eyebrow={justPublished ? "Published" : "Share form"} title={form.name || "Capture form"}
    description={live ? "Anyone with this link can submit the form. Submissions arrive in Leads." : "This form is not live. Publish it or enable its link before sharing."}
    footer={<><a className="leads-secondary-button" href={link} target="_blank" rel="noreferrer"><i className="ti ti-external-link" />Open form</a><button type="button" className="leads-primary-button" onClick={onClose}>Done</button></>}>
    {justPublished && <div className="leads-inline-alert is-success"><i className="ti ti-circle-check" /><span>Your form is live and ready to collect leads.</span></div>}
    {!live && <div className="leads-inline-alert is-warning"><i className="ti ti-alert-triangle" /><span>Visitors will see “Form unavailable” until the form is published.</span></div>}
    <div className="leads-segmented" role="tablist" aria-label="Share options">
      {[["link", "Link", "ti-link"], ["qr", "QR code", "ti-qrcode"], ["embed", "Embed", "ti-code"]].map(([key, label, icon]) => <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? "is-active" : ""} onClick={() => setTab(key)}><i className={`ti ${icon}`} />{label}</button>)}
    </div>
    <div className="leads-share-tracking">
      <label className="leads-field"><span>Campaign <em>optional</em></span><input value={campaign} onChange={(event) => setCampaign(event.target.value)} maxLength={120} placeholder="e.g. spring-launch" /></label>
      <label className="leads-field"><span>Source</span><select value={source} onChange={(event) => setSource(event.target.value)}>{sources.filter((item) => item !== "Social Comment").map((item) => <option key={item}>{item}</option>)}</select></label>
    </div>
    {tab === "link" && <>
      <div className="leads-copy-field"><code>{link}</code><button type="button" className="leads-primary-button" onClick={() => copy(link, "Capture link copied.")}><i className="ti ti-copy" />Copy</button></div>
      <p className="leads-eyebrow leads-share-heading">Share to</p>
      <div className="leads-share-targets">
        {targets.map(([label, icon, href]) => <a key={label} href={href} target="_blank" rel="noreferrer" className="leads-share-target"><i className={`ti ${icon}`} /><span>{label}</span></a>)}
        <button type="button" className="leads-share-target" onClick={share}><i className="ti ti-share" /><span>More</span></button>
      </div>
    </>}
    {tab === "qr" && <div className="leads-qr">
      <div className="leads-qr-code" ref={qrRef}><QRCodeSVG value={link || " "} size={208} level="M" includeMargin /></div>
      <div className="leads-qr-actions"><button type="button" className="leads-secondary-button" onClick={downloadQr}><i className="ti ti-download" />Download SVG</button><button type="button" className="leads-secondary-button" onClick={() => window.print()}><i className="ti ti-printer" />Print</button></div>
    </div>}
    {tab === "embed" && <>
      <p className="leads-modal-note">Paste this snippet into your website's HTML where the form should appear.</p>
      <div className="leads-copy-field is-code"><code>{embed}</code><button type="button" className="leads-primary-button" onClick={() => copy(embed, "Embed code copied.")}><i className="ti ti-copy" />Copy</button></div>
    </>}
  </LeadModal>;
};

export default ShareFormModal;
